import React from 'react';
import {
    Box, Typography, Grid, Card, CardContent, Avatar, Chip,
    Paper, Button, Divider, Stack, LinearProgress,
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    PendingActions as PendingActionsIcon,
    CheckCircleOutline as ApprovedIcon,
    CancelOutlined as RejectedIcon,
    ArrowForward as ArrowIcon,
    People as TeamIcon,
} from '@mui/icons-material';

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, subtitle, icon: Icon, gradient, onClick }) {
    return (
        <Card onClick={onClick} sx={{
            borderRadius: 3,
            cursor: onClick ? 'pointer' : 'default',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: gradient,
            height: '100%',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': onClick ? { transform: 'translateY(-3px)', boxShadow: '0 8px 28px rgba(0,0,0,0.14)' } : {},
        }}>
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.8)', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {label}
                        </Typography>
                        <Typography sx={{ fontSize: 34, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
                            {value}
                        </Typography>
                        {subtitle && (
                            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>{subtitle}</Typography>
                        )}
                    </Box>
                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1.25, flexShrink: 0 }}>
                        <Icon sx={{ fontSize: 24, color: '#fff' }} />
                    </Box>
                </Box>
                {onClick && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
                        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>View details</Typography>
                        <ArrowIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }} />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

// ─── Status Chip ──────────────────────────────────────────────────────────────
const STATUS_COLORS = {
    Draft:             { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1' },
    Submitted:         { bg: '#eff6ff', color: '#3b82f6', border: '#93c5fd' },
    Approved:          { bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
    Rejected:          { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
    'Pending Level 2': { bg: '#fff7ed', color: '#ea580c', border: '#fdba74' },
};
function StatusChip({ status }) {
    const c = STATUS_COLORS[status] || STATUS_COLORS.Draft;
    const label = status === 'Pending Level 2' ? 'Pending External Approval' : status;
    return <Chip label={label} size="small" sx={{ bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, fontWeight: 600, fontSize: 11 }} />;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ManagerTimesheetOverview({ timesheets, onTabChange, isExternalApprover }) {
    const submitted      = timesheets.filter(t => t.status === 'Submitted').length;
    const pendingL2      = timesheets.filter(t => t.status === 'Pending Level 2').length;
    const totalPending   = submitted + pendingL2;
    const approved       = timesheets.filter(t => t.status === 'Approved').length;
    const rejected       = timesheets.filter(t => t.status === 'Rejected').length;

    // Per-member summary
    const memberMap = {};
    timesheets.forEach(t => {
        if (!memberMap[t.employeeId]) {
            memberMap[t.employeeId] = {
                id: t.employeeId,
                name: t.employeeName,
                avatar: t.employeeAvatar,
                role: t.employeeRole,
                totalHours: 0,
                submitted: 0,
                approved: 0,
                pending: 0,
            };
        }
        const m = memberMap[t.employeeId];
        m.totalHours += t.totalHours;
        if (t.status === 'Approved') m.approved++;
        else if (t.status === 'Submitted' || t.status === 'Pending Level 2') m.pending++;
        else if (t.status === 'Submitted') m.submitted++;
    });
    const members = Object.values(memberMap).sort((a, b) => b.totalHours - a.totalHours);

    // Pending items for action — L2 queue for external approver, submitted for manager
    const pendingItems = timesheets
        .filter(t => isExternalApprover ? t.status === 'Pending Level 2' : t.status === 'Submitted')
        .sort((a, b) => new Date(b.submittedOn) - new Date(a.submittedOn))
        .slice(0, 5);

    return (
        <Box>
            {/* ── Stat Cards ──────────────────────────────────────────── */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Total Timesheets" value={timesheets.length}
                        subtitle="All team timesheets"
                        icon={AccessTimeIcon} gradient="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
                        onClick={() => onTabChange(1)} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        label="Pending Review"
                        value={isExternalApprover ? pendingL2 : totalPending}
                        subtitle={
                            (isExternalApprover ? pendingL2 : totalPending) === 0
                                ? 'All caught up'
                                : isExternalApprover
                                    ? `${pendingL2} awaiting your sign-off`
                                    : `${submitted} submitted · ${pendingL2} awaiting ext. review`
                        }
                        icon={PendingActionsIcon} gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                        onClick={() => onTabChange(1, 'Submitted')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Approved" value={approved}
                        subtitle="Fully approved"
                        icon={ApprovedIcon} gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                        onClick={() => onTabChange(1, 'Approved')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Rejected" value={rejected}
                        subtitle="Returned to employees"
                        icon={RejectedIcon} gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                        onClick={() => onTabChange(1, 'Rejected')} />
                </Grid>
            </Grid>

            <Grid container spacing={2.5}>
                {/* ── Team Member Summary ──────────────────────────── */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', height: '100%' }}>
                        <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TeamIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>Team Summary</Typography>
                            </Box>
                            {!isExternalApprover && (
                                <Button size="small" endIcon={<ArrowIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => onTabChange(2)}
                                    sx={{ fontSize: 12, color: '#6366f1', fontWeight: 600, textTransform: 'none' }}>
                                    Analytics
                                </Button>
                            )}
                        </Box>
                        <Box>
                            {members.map((m, i) => {
                                const totalTs = timesheets.filter(t => t.employeeId === m.id).length;
                                const approvalRate = totalTs > 0 ? Math.round((m.approved / totalTs) * 100) : 0;
                                return (
                                    <Box key={m.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.75 }}>
                                            <Avatar sx={{ width: 34, height: 34, bgcolor: '#6366f1', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                                                {m.avatar}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{m.name}</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                    <LinearProgress variant="determinate" value={approvalRate}
                                                        sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: '#e2e8f0',
                                                            '& .MuiLinearProgress-bar': { bgcolor: approvalRate >= 70 ? '#10b981' : '#f59e0b' } }} />
                                                    <Typography sx={{ fontSize: 11, color: '#64748b', flexShrink: 0 }}>{approvalRate}%</Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{m.totalHours}h</Typography>
                                                {m.pending > 0 && (
                                                    <Chip label={`${m.pending} pending`} size="small"
                                                        sx={{ bgcolor: '#fff7ed', color: '#ea580c', fontSize: 10, fontWeight: 700, height: 17, mt: 0.25 }} />
                                                )}
                                            </Box>
                                        </Box>
                                        {i < members.length - 1 && <Divider />}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                </Grid>

                {/* ── Needs Your Review ───────────────────────────── */}
                <Grid size={{ xs: 12, md: 7 }}>
                    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', height: '100%' }}>
                        <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PendingActionsIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>Needs Your Review</Typography>
                                {submitted > 0 && (
                                    <Chip label={submitted} size="small"
                                        sx={{ bgcolor: '#fff7ed', color: '#ea580c', fontWeight: 700, fontSize: 11, height: 20 }} />
                                )}
                            </Box>
                            <Button size="small" endIcon={<ArrowIcon sx={{ fontSize: 14 }} />}
                                onClick={() => onTabChange(1, isExternalApprover ? 'Pending Level 2' : 'Submitted')}
                                sx={{ fontSize: 12, color: '#6366f1', fontWeight: 600, textTransform: 'none' }}>
                                Review All
                            </Button>
                        </Box>
                        {pendingItems.length === 0 ? (
                            <Box sx={{ py: 6, textAlign: 'center' }}>
                                <ApprovedIcon sx={{ fontSize: 40, color: '#86efac', mb: 1 }} />
                                <Typography sx={{ color: '#16a34a', fontWeight: 600, fontSize: 14 }}>All caught up!</Typography>
                                <Typography sx={{ color: '#94a3b8', fontSize: 12, mt: 0.5 }}>No timesheets pending your review.</Typography>
                            </Box>
                        ) : (
                            <Box>
                                {pendingItems.map((ts, i) => (
                                    <Box key={ts.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.75 }}>
                                            <Avatar sx={{ width: 36, height: 36, bgcolor: '#6366f1', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                                                {ts.employeeAvatar}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{ts.employeeName}</Typography>
                                                    <Chip label={ts.employeeCode || ts.employeeId} size="small"
                                                        sx={{ bgcolor: '#eef2ff', color: '#6366f1', fontSize: 10, fontWeight: 700, height: 17 }} />
                                                </Stack>
                                                <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                                                    {ts.weekStart} – {ts.weekEnd} · {ts.totalHours}h total
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                <StatusChip status={ts.status} />
                                                <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.5 }}>
                                                    Submitted {ts.submittedOn}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {i < pendingItems.length - 1 && <Divider />}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
